import React from 'react';
import UneditedMessageWarning from '@apps/registry/common/components/ShoppingCart/components/UneditedMessageWarning';
import { Checkbox } from '@shared/components';
import { GiftWrapEditor } from '@shared/components/GiftWrap';
import { SpacingStack, TextV2, Box, Flex, Divider, TextArea, ButtonV2, InputV2 } from '@withjoy/joykit';
import { CheckboxContainer } from './Offer.styles';
import { useOfferController } from './Offer.controller';
import { GetDataForThanksForRSVPQuery } from '@graphql/generated';
import { Wrapper } from './components/Wrapper';
import { filledButtonOverrides } from '../BasicNote/BasicNote.styles';

interface OfferProps {
  message: string;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  eventHandle: string;
  queryData?: GetDataForThanksForRSVPQuery;
}

export const Offer: React.FC<OfferProps> = ({ message, setMessage, eventHandle, queryData }) => {
  const {
    setThemeId,
    price,
    isLoading,
    tCongratulationsCard,
    THEMES,
    prompt,
    promptFallback,
    handleBlur,
    handleEditMessageClick,
    tab,
    uneditedMessageWarning,
    isGeneratedMessageLoading,
    noEventId,
    guestEmail,
    email,
    setEmail,
    errorMessage,
    handleSend,
    isEmptyEmail,
    handleWrapClick,
    handleMessageClick,
    handleContinueClick,
    handleOnChangeTextArea,
    handleOnBlurTextArea,
  } = useOfferController(eventHandle, message, setMessage, queryData);

  return (
    <Flex
      marginTop="32px"
      flexDirection="column"
      maxWidth="475px"
      width="100%"
    >
      <SpacingStack
        spacing={3}
        paddingX={0}
        paddingTop={0}
      >
        <UneditedMessageWarning
          isRsvp
          isOpen={uneditedMessageWarning.isOpen}
          onClose={uneditedMessageWarning.onClose}
          onContinue={handleContinueClick}
        />
        <Box
          border="1px solid"
          borderColor="mono3"
          borderRadius="4px"
          paddingTop={1}
        >
          <Flex
            alignItems="center"
            padding={5}
            role="button"
            onClick={handleMessageClick}
            cursor="pointer"
          >
            <CheckboxContainer>
              <Checkbox
                id="basic"
                checked={tab === 'message'}
                onChange={handleMessageClick}
              />
            </CheckboxContainer>
            <Flex
              flexDirection="column"
              marginLeft={5}
              flexGrow={1}
            >
              <Flex
                justifyContent="space-between"
                marginBottom={1}
              >
                <TextV2 typographyVariant="button1">{tCongratulationsCard.noteTitle()}</TextV2>
                <TextV2
                  typographyVariant="label2"
                  color="mono10"
                >
                  {tCongratulationsCard.free()}
                </TextV2>
              </Flex>
            </Flex>
          </Flex>
          {tab === 'message' && (
            <Box
              paddingBottom={5}
              paddingX={5}
            >
              <Flex paddingBottom={5}>
                <Divider />
              </Flex>
              <TextV2
                typographyVariant="label3"
                marginBottom={3}
              >
                {tCongratulationsCard.noteTitle()}
              </TextV2>
              <TextArea
                minRows={5}
                placeholder={tCongratulationsCard.notePlaceholder()}
                value={message}
                onChange={handleOnChangeTextArea}
                onBlur={handleOnBlurTextArea}
                disabled={isGeneratedMessageLoading}
              />
            </Box>
          )}
        </Box>
        <Box
          border="1px solid"
          borderColor="mono3"
          borderRadius="4px"
          paddingTop={1}
        >
          <Flex
            alignItems="center"
            padding={5}
            role="button"
            onClick={handleWrapClick}
            cursor="pointer"
          >
            <CheckboxContainer>
              <Checkbox
                id="card"
                checked={tab === 'wrap'}
                onChange={handleWrapClick}
              />
            </CheckboxContainer>
            <Flex
              flexDirection="column"
              marginLeft={5}
              flexGrow={1}
            >
              <Flex
                justifyContent="space-between"
                marginBottom={1}
              >
                <TextV2 typographyVariant="button1">{tCongratulationsCard.wrapTitle()}</TextV2>
                <TextV2
                  typographyVariant="label2"
                  color="mono10"
                >
                  {price}
                </TextV2>
              </Flex>
              <TextV2
                typographyVariant="label2"
                color="mono12"
              >
                {tCongratulationsCard.wrapDescription()}
              </TextV2>
            </Flex>
          </Flex>
          <Wrapper
            handleWrapClick={handleWrapClick}
            isMessageActive={tab === 'message'}
          >
            <Flex
              paddingBottom={5}
              paddingX={5}
            >
              <Divider />
            </Flex>
            <GiftWrapEditor
              message={message}
              setMessage={setMessage}
              themes={THEMES}
              setThemeId={setThemeId}
              prompt={prompt}
              promptFallback={promptFallback}
              isLoading={isLoading}
              onBlur={handleBlur}
              onEditMessageClick={handleEditMessageClick}
              skip
            />
          </Wrapper>
        </Box>
      </SpacingStack>
      <Flex
        marginTop="32px"
        flexDirection="column"
      >
        {!guestEmail && (
          <>
            <TextV2
              typographyVariant="label3"
              marginBottom={3}
            >
              {tCongratulationsCard.yourEmail()}
            </TextV2>
            <InputV2
              placeholder="youremail@website.com"
              onChange={e => setEmail(e.target.value)}
              value={email}
            ></InputV2>
          </>
        )}
        {!!errorMessage && (
          <TextV2
            typographyVariant="body2"
            color="negative6"
          >
            {tCongratulationsCard.error({ msg: errorMessage })}
          </TextV2>
        )}
        <ButtonV2
          size="lg"
          onClick={() => handleSend()}
          disabled={noEventId || !message || isEmptyEmail}
          loading={isLoading || isGeneratedMessageLoading}
          overrides={filledButtonOverrides}
          marginTop="48px"
        >
          {tab === 'message' ? tCongratulationsCard.sendMessage() : tCongratulationsCard.continueToPurchase()}
        </ButtonV2>
      </Flex>
    </Flex>
  );
};
